import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { buildImageUrl } from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { formatDateTime } from "@/lib/datetime";
import StatusBadge from "@/components/StatusBadge";
import { STOCK } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import { ImagePreview, useImagePreview } from "@/components/ImagePreview";
import {
  Search, ImageOff, Image as ImageIcon, Download, ChevronLeft, ChevronRight, Loader2, Star, X, Coins, Layers2, Printer,
} from "lucide-react";
import HighlightMatch from "@/components/HighlightMatch";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const PAGE_SIZE = 200;
const SEARCH_SUGGESTION_LIMIT = 10;

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function formatDate(iso) {
  return formatDateTime(iso) || "—";
}

export default function StockMonitoring() {
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tab, setTab] = useState("products");

  // Common filters — "pending" values are what the user is typing / picking.
  // We only push these into `committed` when Search is clicked, so nothing
  // loads on typing.
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [supplierId, setSupplierId] = useState("all");
  const [branchCode, setBranchCode] = useState("all");
  const [stockStatus, setStockStatus] = useState("all"); // all | available | negative | low | out
  const [isPremium, setIsPremium] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [hasImageOnly, setHasImageOnly] = useState(false);
  const [committed, setCommitted] = useState(null);
  const effectiveSearch = committed ? (committed.search || "").trim() : "";

  // Pagination for product view
  const [page, setPage] = useState(1);
  const [productData, setProductData] = useState({ items: [], total: 0, total_pages: 1 });
  const [prodLoading, setProdLoading] = useState(false);

  // Pagination for branch matrix
  const [matrixPage, setMatrixPage] = useState(1);
  const [matrix, setMatrix] = useState({ branches: [], rows: [], total: 0, total_pages: 1 });
  const [matrixLoading, setMatrixLoading] = useState(false);

  const [fastMoving, setFastMoving] = useState([]);
  const [productView, setProductView] = useState("table"); // table | grid
  const [matrixView, setMatrixView] = useState("table");   // table | grid
  const [exporting, setExporting] = useState(false);
  const preview = useImagePreview();

  // ── Group View (multi-select compare) ─────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  const [groupOpen, setGroupOpen] = useState(false);
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const openImagePreview = async (productId, name) => {
    try {
      const res = await api.get(`/products/${productId}`);
      const imgs = (res.data.images || []).map((im) => ({ ...im, name }));
      if (imgs.length === 0) return;
      preview.open(imgs, 0);
    } catch (e) {
      // Silently skip preview if we can't fetch the product — the grid still
      // works, we just don't open the modal. Log to the console so a curious
      // dev can see why the click was a no-op.
      // eslint-disable-next-line no-console
      console.debug("openImagePreview: fetch failed", e);
    }
  };

  // Load reference data once
  useEffect(() => {
    (async () => {
      const [b, c, s] = await Promise.all([
        api.get("/branches?only_active=true"),
        api.get("/categories"),
        api.get("/suppliers"),
      ]);
      setBranches(b.data);
      setCategories(c.data);
      setSuppliers(s.data);
    })();
  }, []);

  // Reset page when the committed filter set changes
  useEffect(() => { setPage(1); setMatrixPage(1); }, [committed]);

  const runSearch = () => {
    setCommitted({
      search, category, supplierId, branchCode, stockStatus,
      isPremium, includeInactive, hasImageOnly,
    });
  };
  const resetSearch = () => {
    setSearch(""); setCategory("all"); setSupplierId("all"); setBranchCode("all"); setStockStatus("all");
    setIsPremium(false); setIncludeInactive(false); setHasImageOnly(false);
    setCommitted(null);
    setProductData({ items: [], total: 0, total_pages: 1 });
    setMatrix({ branches: [], rows: [], total: 0, total_pages: 1 });
  };

  // Load product-centric stock view (paginated)
  useEffect(() => {
    if (tab !== "products" && tab !== "low" && tab !== "damaged") return;
    if (!committed) return;   // wait for Search
    (async () => {
      setProdLoading(true);
      try {
        const params = { page, page_size: effectiveSearch ? SEARCH_SUGGESTION_LIMIT : PAGE_SIZE };
        if (effectiveSearch) params.search = effectiveSearch;
        if (committed.category !== "all") params.category = committed.category;
        if (committed.supplierId !== "all") params.supplier_id = committed.supplierId;
        if (tab === "low") params.stock_status = "low";
        else if (tab === "damaged") params.stock_status = "damaged";
        else if (committed.stockStatus !== "all") params.stock_status = committed.stockStatus;
        if (committed.isPremium) params.is_premium = true;
        if (committed.includeInactive) params.include_inactive = true;
        if (committed.hasImageOnly) params.has_image = true;
        const res = await api.get("/stock/products", { params });
        setProductData(res.data);
      } finally {
        setProdLoading(false);
      }
    })();
  }, [tab, page, committed, effectiveSearch]);

  // Load branch matrix (paginated)
  useEffect(() => {
    if (tab !== "branch-matrix") return;
    if (!committed) return;   // wait for Search
    (async () => {
      setMatrixLoading(true);
      try {
        const params = { page: matrixPage, page_size: effectiveSearch ? SEARCH_SUGGESTION_LIMIT : PAGE_SIZE };
        if (effectiveSearch) params.search = effectiveSearch;
        if (committed.category !== "all") params.category = committed.category;
        if (committed.supplierId !== "all") params.supplier_id = committed.supplierId;
        if (committed.branchCode !== "all") params.branch_code = committed.branchCode;
        if (committed.stockStatus !== "all") params.stock_status = committed.stockStatus;
        if (committed.isPremium) params.is_premium = true;
        if (committed.includeInactive) params.include_inactive = true;
        if (committed.hasImageOnly) params.has_image = true;
        const res = await api.get("/stock/branch-matrix", { params });
        setMatrix(res.data);
      } finally {
        setMatrixLoading(false);
      }
    })();
  }, [tab, matrixPage, committed, effectiveSearch]);

  // Fast-moving — this tab is independent of the filter bar; it always loads
  // (light payload, drives a small chart).
  useEffect(() => {
    if (tab !== "fast") return;
    (async () => {
      const params = { limit: 15 };
      if (branchCode !== "all") params.branch_code = branchCode;
      const res = await api.get("/dashboard/fast-moving", { params });
      setFastMoving(res.data);
    })();
  }, [tab, branchCode]);

  const exportCsv = async () => {
    if (!committed) { toast.error("Apply filters and click Search first"); return; }
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      if (effectiveSearch) params.search = effectiveSearch;
      if (committed.category !== "all") params.category = committed.category;
      if (committed.supplierId !== "all") params.supplier_id = committed.supplierId;
      const isBranchTab = tab === "branch-matrix";
      if (isBranchTab) {
        if (committed.branchCode !== "all") params.branch_code = committed.branchCode;
      }
      if (tab === "low") params.stock_status = "low";
      else if (tab === "damaged") params.stock_status = "damaged";
      else if (committed.stockStatus !== "all") params.stock_status = committed.stockStatus;
      if (committed.isPremium) params.is_premium = true;
      if (committed.includeInactive) params.include_inactive = true;
      if (committed.hasImageOnly) params.has_image = true;

      const url = isBranchTab ? "/stock/branch-matrix.csv" : "/stock/products.csv";
      toast.info("Preparing CSV — may take up to 30 seconds for the full catalogue…");
      const res = await api.get(url, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = isBranchTab
        ? `thara-branch-stock-${new Date().toISOString().slice(0, 10)}.csv`
        : `thara-stock-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success("CSV downloaded — all pages included");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount =
    (search ? 1 : 0)
    + (category !== "all" ? 1 : 0)
    + (supplierId !== "all" ? 1 : 0)
    + (branchCode !== "all" && tab === "branch-matrix" ? 1 : 0)
    + (includeInactive ? 1 : 0)
    + (hasImageOnly ? 1 : 0);

  const clearAllFilters = resetSearch;

  return (
    <div className="space-y-2" data-testid={STOCK.container}>
      {/* Compact inline filter bar — no drawer */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-44">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search…"
            className="pl-8 pr-7 h-9 text-sm bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            data-testid={STOCK.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="w-52">
          <SearchableSelect
            value={category}
            onChange={setCategory}
            options={categories.map((c) => ({ value: c.name, label: c.name }))}
            placeholder="All Categories"
            allLabel="All Categories"
            testId="stock-category-filter"
          />
        </div>

        <div className="w-52">
          <SearchableSelect
            value={supplierId}
            onChange={setSupplierId}
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            placeholder="All Suppliers"
            allLabel="All Suppliers"
            testId="stock-supplier-filter"
          />
        </div>

        {tab === "branch-matrix" && (
          <Select value={branchCode} onValueChange={setBranchCode}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid={STOCK.branchFilter}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Icon toggles */}
        <button
          type="button"
          onClick={() => setIsPremium((v) => !v)}
          className={`h-9 w-9 rounded-md border flex items-center justify-center transition-all ${
            isPremium
              ? "bg-amber-50 border-amber-300 text-amber-700"
              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
          data-testid="stock-premium-toggle"
          title={isPremium ? "Showing Premium only" : "Show Premium only"}
          aria-label="Premium filter"
        >
          <Star className={`h-4 w-4 ${isPremium ? "fill-amber-400 text-amber-500" : ""}`} />
        </button>

        <button
          type="button"
          onClick={() => setIncludeInactive((v) => !v)}
          className={`hidden h-9 px-2.5 rounded-md border text-xs font-semibold items-center gap-1.5 transition-all ${
            includeInactive
              ? "bg-gray-900 border-gray-900 text-white"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="stock-include-inactive-toggle"
          title={includeInactive ? "Including inactive products" : "Show inactive products"}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${includeInactive ? "bg-emerald-400" : "bg-gray-300"}`} />
          Inactive
        </button>

        <button
          type="button"
          onClick={() => setHasImageOnly((v) => !v)}
          className={`hidden h-9 px-2.5 rounded-md border text-xs font-semibold items-center gap-1.5 transition-all ${
            hasImageOnly
              ? "bg-[#9B1C1C] border-[#9B1C1C] text-white"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="stock-has-image-toggle"
          title={hasImageOnly ? "Showing only products with images" : "Show only products with images"}
        >
          <ImageIcon className="h-3.5 w-3.5" />
          Images only
        </button>

        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="h-9 px-2 text-xs font-semibold text-[#9B1C1C] hover:underline"
            data-testid="stock-clear-filters"
          >
            Clear ({activeFilterCount})
          </button>
        )}

        <Button size="sm" className="h-9 gap-1.5 bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={runSearch} data-testid="stock-search-btn">
          <Search className="h-4 w-4" /> Search
        </Button>
        <Button size="sm" variant="outline" className="h-9" onClick={resetSearch} data-testid="stock-reset-btn">
          Reset
        </Button>
        {selected.size > 0 && (
          <Button
            size="sm"
            className="h-9 gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white"
            onClick={() => setGroupOpen(true)}
            data-testid="stock-group-view-btn"
          >
            <Layers2 className="h-4 w-4" /> Group View ({selected.size})
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {committed
              ? `${(tab === "branch-matrix" ? matrix.total : productData.total).toLocaleString()} rows`
              : "Apply filters and click Search"}
          </span>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting || tab === "fast"}
            className="h-9 w-9 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-gray-600"
            data-testid="stock-export-csv"
            title="Export CSV (all pages)"
            aria-label="Export CSV"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="products" data-testid={STOCK.tabOverall}>Product View</TabsTrigger>
          <TabsTrigger value="branch-matrix" data-testid={STOCK.tabBranchWise}>Branch-wise Stock</TabsTrigger>
          <TabsTrigger value="low" data-testid={STOCK.tabLow}>Low Stock</TabsTrigger>
          <TabsTrigger value="fast" data-testid={STOCK.tabFastMoving}>Fast Moving</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="pt-2 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <StockStatusFilter value={stockStatus} onChange={setStockStatus} />
            <ViewToggle value={productView} onChange={setProductView} testId="stock-product-view-toggle" />
          </div>
          {productView === "table" ? (
            <ProductStockTable data={productData} loading={prodLoading} page={page} setPage={setPage} startIndex={(page - 1) * PAGE_SIZE} onImageClick={openImagePreview} highlightQuery={effectiveSearch} selected={selected} onToggleSelect={toggleSelect} />
          ) : (
            <ProductStockGrid data={productData} loading={prodLoading} page={page} setPage={setPage} onImageClick={openImagePreview} highlightQuery={effectiveSearch} />
          )}
        </TabsContent>

        <TabsContent value="branch-matrix" className="pt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <StockStatusFilter value={stockStatus} onChange={setStockStatus} />
            <ViewToggle value={matrixView} onChange={setMatrixView} testId="stock-matrix-view-toggle" />
            <p className="text-xs text-gray-500 ml-auto">
              {matrix.branches.length} branch{matrix.branches.length !== 1 ? "es" : ""}
              {branchCode !== "all" && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                  {branches.find((b) => b.code === branchCode)?.name || branchCode}
                  <button onClick={() => setBranchCode("all")} className="hover:text-[#9B1C1C]" aria-label="Clear branch filter">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </p>
          </div>
          {matrixView === "table" ? (
            <BranchMatrixTable
              matrix={matrix}
              loading={matrixLoading}
              page={matrixPage}
              setPage={setMatrixPage}
              startIndex={(matrixPage - 1) * PAGE_SIZE}
              onImageClick={openImagePreview}
              highlightQuery={effectiveSearch}
              selected={selected}
              onToggleSelect={toggleSelect}
            />
          ) : (
            <BranchMatrixGrid matrix={matrix} loading={matrixLoading} page={matrixPage} setPage={setMatrixPage} onImageClick={openImagePreview} highlightQuery={effectiveSearch} />
          )}
        </TabsContent>

        <TabsContent value="low" className="pt-4">
          <ProductStockTable data={productData} loading={prodLoading} page={page} setPage={setPage} startIndex={(page - 1) * PAGE_SIZE} highlight="low" onImageClick={openImagePreview} highlightQuery={effectiveSearch} selected={selected} onToggleSelect={toggleSelect} />
        </TabsContent>

        <TabsContent value="fast" className="pt-4 space-y-6">
          <Surface padded={false} className="p-4">
            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Branch</p>
              <Select value={branchCode} onValueChange={setBranchCode}>
                <SelectTrigger className="w-64 h-9" data-testid={STOCK.branchFilter}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Surface>

          <Surface padded={false}>
            <div className="p-6 pb-2">
              <h3 className="font-heading text-base font-semibold text-gray-900">Top 15 Fast Movers</h3>
              <p className="text-xs text-gray-500 mt-0.5">Units sold in the last 30 days</p>
            </div>
            <div style={{ height: 360, minHeight: 360 }} className="p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fastMoving} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#6B7280", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#374151", fontSize: 11 }} width={200} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Bar dataKey="sold_30d" fill="#047857" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Surface>
        </TabsContent>
      </Tabs>

      {preview.state.open && (
        <ImagePreview
          images={preview.state.images}
          initialIndex={preview.state.index}
          onClose={preview.close}
        />
      )}
      {groupOpen && (
        <GroupViewDialog
          ids={Array.from(selected)}
          onClose={() => { setGroupOpen(false); clearSelection(); }}
        />
      )}
    </div>
  );
}

function useHorizontalScroll() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Mouse-wheel: vertical scroll on the container translates to horizontal.
    const onWheel = (e) => {
      // Only intervene when there's horizontal overflow.
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    // Keyboard: Arrow-Left / Right, PageUp/Down when the container is focused or hovered.
    const onKey = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const step = 120;
      if (e.key === "ArrowRight") { el.scrollLeft += step; e.preventDefault(); }
      else if (e.key === "ArrowLeft") { el.scrollLeft -= step; e.preventDefault(); }
      else if (e.key === "PageDown" || e.key === "PageUp") {
        el.scrollLeft += (e.key === "PageDown" ? 1 : -1) * el.clientWidth * 0.85;
        e.preventDefault();
      } else if (e.key === "Home") { el.scrollLeft = 0; e.preventDefault(); }
      else if (e.key === "End") { el.scrollLeft = el.scrollWidth; e.preventDefault(); }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("keydown", onKey);
    // Make container focusable so keyboard scrolling actually receives events.
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("keydown", onKey);
    };
  }, []);
  return ref;
}

function StockStatusFilter({ value, onChange }) {
  const opts = [
    { key: "all", label: "All Stock", hint: "everything" },
    { key: "available", label: "Available", hint: "> 0 in stock" },
    { key: "out", label: "Out of Stock", hint: "= 0" },
    { key: "negative", label: "Negative Stock", hint: "< 0" },
  ];
  return (
    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-1 w-fit" data-testid="stock-status-filter">
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
              active
                ? "bg-[#9B1C1C] text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`stock-status-${o.key}`}
            title={o.hint}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ViewToggle({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1 border border-gray-200 rounded-md p-0.5 bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange("table")}
        className={`px-3 h-8 text-xs font-medium rounded ${value === "table" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}
        data-testid={`${testId}-table`}
      >Table</button>
      <button
        type="button"
        onClick={() => onChange("grid")}
        className={`px-3 h-8 text-xs font-medium rounded ${value === "grid" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}
        data-testid={`${testId}-grid`}
      >Grid</button>
    </div>
  );
}

function PremiumToggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-semibold transition-all ${
        value
          ? "bg-amber-50 border-amber-300 text-amber-800"
          : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
      }`}
      data-testid="stock-premium-toggle"
      title="Show only products marked as Premium"
    >
      <Star className={`h-3.5 w-3.5 ${value ? "fill-amber-400 text-amber-500" : "text-gray-400"}`} />
      Premium only
    </button>
  );
}

function ThumbCell({ path, name, onClick }) {
  const clickable = !!path && !!onClick;
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`h-9 w-9 rounded-md bg-gradient-to-br from-amber-50 to-red-50 overflow-hidden flex items-center justify-center border border-gray-200 shrink-0 ${
        clickable ? "cursor-pointer hover:ring-2 hover:ring-[#9B1C1C]/40 transition" : "cursor-default"
      }`}
      title={clickable ? "Click to preview" : ""}
      data-testid={clickable ? "stock-thumb-clickable" : "stock-thumb"}
    >
      {path ? (
        <img src={buildImageUrl(path)} alt={name} className="w-full h-full object-cover" />
      ) : (
        <ImageOff className="h-4 w-4 text-gray-300" />
      )}
    </button>
  );
}

function PaginationBar({ page, totalPages, total, setPage }) {
  if (totalPages <= 1) return (
    <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
      {total.toLocaleString()} record{total !== 1 ? "s" : ""}
    </div>
  );
  const canPrev = page > 1;
  const canNext = page < totalPages;
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-2 flex items-center gap-3 text-xs text-gray-600">
      <span data-testid="stock-pagination-info">
        Page <span className="font-semibold text-gray-900">{page}</span> of {totalPages.toLocaleString()} · {total.toLocaleString()} record{total !== 1 ? "s" : ""}
      </span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(1)} data-testid="stock-pagination-first">« First</Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(page - 1)} data-testid="stock-pagination-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(page + 1)} data-testid="stock-pagination-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(totalPages)} data-testid="stock-pagination-last">Last »</Button>
      </div>
    </div>
  );
}

function ProductStockGrid({ data, loading, page, setPage, onImageClick, highlightQuery }) {
  const rows = data.items || [];
  return (
    <div className="space-y-3">
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex items-center justify-center rounded-md">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {rows.map((p) => {
            const isOut = p.total_stock <= 0;
            const isLow = !isOut && p.total_stock < p.reorder_level;
            const isNeg = p.total_stock < 0;
            return (
              <div key={p.id} className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`stock-product-card-${p.id}`}>
                <button
                  type="button"
                  onClick={() => p.image_path && onImageClick && onImageClick(p.id, p.name)}
                  disabled={!p.image_path}
                  className="aspect-square bg-gradient-to-br from-amber-50 to-red-50 relative overflow-hidden"
                  data-testid={p.image_path ? `stock-card-thumb-${p.id}` : undefined}
                >
                  {p.image_path ? (
                    <img src={buildImageUrl(p.image_path)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageOff className="h-10 w-10 text-gray-300" />
                    </div>
                  )}
                  {p.price_code && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-mono text-[11px] font-semibold tracking-wider shadow">
                      {p.price_code}
                    </span>
                  )}
                </button>
                <div className="p-3 flex-1 flex flex-col gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{p.category}</p>
                    <p className="font-heading font-semibold text-gray-900 leading-tight line-clamp-2" title={p.name}>
                      <HighlightMatch text={p.name} query={highlightQuery} />
                    </p>
                    <p className="text-[11px] text-gray-500 font-mono mt-0.5">{p.code}</p>
                  </div>
                  {p.suppliers && p.suppliers.length > 0 && (
                    <p className="text-[11px] text-gray-600 truncate" title={p.suppliers.map((s) => s.name).join(", ")}>
                      <span className="text-gray-400">by</span> <span className="font-medium text-gray-800">{p.suppliers[0].name}</span>
                      {p.suppliers.length > 1 && <span className="text-gray-400"> +{p.suppliers.length - 1}</span>}
                    </p>
                  )}
                  {p.supplier_aliases && p.supplier_aliases.length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`stock-aliases-${p.id}`}>
                      {p.supplier_aliases.slice(0, 3).map((a) => (
                        <span
                          key={a.supplier_id}
                          title={`${a.supplier_name || "Supplier"}: ${a.alias_name}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-medium text-blue-800 max-w-[160px] truncate"
                        >
                          <HighlightMatch text={a.alias_name} query={highlightQuery} />
                        </span>
                      ))}
                      {p.supplier_aliases.length > 3 && (
                        <span className="text-[10px] text-gray-400">+{p.supplier_aliases.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 mt-auto border-t border-gray-100">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500">MRP</p>
                      <p className="font-heading font-bold text-amber-700 text-sm">{formatINR(p.mrp)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500">Stock</p>
                      <p className={`font-mono font-bold text-lg ${isNeg ? "text-red-700" : isOut ? "text-gray-400" : isLow ? "text-amber-700" : "text-emerald-700"}`}>
                        {p.total_stock} <span className="text-xs text-gray-500 font-sans font-normal">{p.unit}</span>
                      </p>
                    </div>
                  </div>
                  {p.rg_parents && p.rg_parents.length > 0 && (
                    <div className="pt-2 border-t border-amber-100 space-y-1">
                      <p className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1">
                        <Coins className="h-3 w-3" /> Raw Material
                      </p>
                      {p.rg_parents.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100">
                          <span className="text-[11px] font-medium text-amber-900 truncate" title={r.name}>
                            {r.name}
                          </span>
                          <span className={`text-[11px] font-mono font-bold ${r.stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                            {r.stock ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && rows.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-gray-500">No matching products</div>
          )}
        </div>
      </div>
      <PaginationBar page={page} totalPages={data.total_pages || 1} total={data.total || 0} setPage={setPage} />
    </div>
  );
}

function BranchMatrixGrid({ matrix, loading, page, setPage, onImageClick, highlightQuery }) {
  const { branches, rows } = matrix;
  return (
    <div className="space-y-3">
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex items-center justify-center rounded-md">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {rows.map((row) => {
            const total = row.total_stock;
            return (
              <div key={row.id} className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`stock-matrix-card-${row.id}`}>
                {/* Full-width image, matches Product card layout */}
                <button
                  type="button"
                  onClick={() => row.image_path && onImageClick && onImageClick(row.id, row.name)}
                  disabled={!row.image_path}
                  className="aspect-square bg-gradient-to-br from-amber-50 to-red-50 relative overflow-hidden"
                  data-testid={row.image_path ? `stock-matrix-card-thumb-${row.id}` : undefined}
                >
                  {row.image_path ? (
                    <img src={buildImageUrl(row.image_path)} alt={row.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageOff className="h-10 w-10 text-gray-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 px-2 py-0.5 rounded font-mono text-[11px] font-bold shadow ${
                    total < 0 ? "bg-red-600 text-white" : total === 0 ? "bg-gray-600 text-white" : "bg-emerald-600 text-white"
                  }`}>
                    Total {total}
                  </span>
                </button>

                {/* Product info */}
                <div className="p-3 pb-2 border-b border-gray-100">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{row.category}</p>
                  <p className="font-heading font-semibold text-gray-900 leading-tight line-clamp-2" title={row.name}>
                    <HighlightMatch text={row.name} query={highlightQuery} />
                  </p>
                  <p className="text-[11px] text-gray-500 font-mono mt-0.5">{row.code} · {row.unit}</p>
                </div>

                {/* Branch × Stock — 2 branches per row (grid-cols-2), each cell has Branch | Stock */}
                <div className="p-3 flex-1">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Branch Stock</p>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">Total {row.total_stock}</p>
                  </div>
                  {row.rg_parents && row.rg_parents.length > 0 && (
                    <div className="mb-3 space-y-1">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 flex items-center gap-1">
                        <Coins className="h-3 w-3" /> Raw Material
                      </p>
                      {row.rg_parents.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100">
                          <span className="text-[11px] font-medium text-amber-900 truncate" title={r.name}>
                            {r.name}
                          </span>
                          <span className={`text-[11px] font-mono font-bold ${r.stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                            {r.stock ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                    {branches.map((b) => {
                      const qty = (row.branches[b.code] || {}).qty || 0;
                      const isZero = qty === 0;
                      const isNeg = qty < 0;
                      return (
                        <div
                          key={b.code}
                          className={`flex items-center justify-between px-2 py-1 rounded border ${
                            isNeg
                              ? "bg-red-50 border-red-200"
                              : isZero
                                ? "bg-gray-50 border-gray-200"
                                : "bg-emerald-50 border-emerald-200"
                          }`}
                          data-testid={`stock-matrix-card-branch-${row.id}-${b.code}`}
                          title={`${b.name}: ${qty}`}
                        >
                          <span className={`text-[11px] font-medium truncate ${
                            isNeg ? "text-red-700" : isZero ? "text-gray-500" : "text-emerald-800"
                          }`}>
                            {b.name}
                          </span>
                          <span className={`font-mono font-bold text-sm ml-2 ${
                            isNeg ? "text-red-700" : isZero ? "text-gray-400" : "text-emerald-800"
                          }`}>
                            {qty}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
          {!loading && rows.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-gray-500">No matching products</div>
          )}
        </div>
      </div>
      <PaginationBar page={page} totalPages={matrix.total_pages || 1} total={matrix.total || 0} setPage={setPage} />
    </div>
  );
}

function ProductStockTable({ data, loading, page, setPage, startIndex, highlight, onImageClick, highlightQuery, selected, onToggleSelect }) {
  const rows = data.items || [];
  const canSelect = typeof onToggleSelect === "function";
  return (
    <Surface padded={false}>
      <div className="relative overflow-auto max-h-[calc(100vh-200px)]">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 shadow-sm">
            <tr className="bg-gray-50 border-b border-gray-200">
              {canSelect && <th className="px-2 py-3 text-center w-8"><span className="sr-only">Select</span></th>}
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-12">#</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Unit</th>
              <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Available</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/30">Raw Material</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Cat / Sub-Cat</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Supplier</th>
              <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">MRP</th>
              <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/40">Price</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => {
              const isLow = p.total_stock > 0 && p.total_stock < p.reorder_level;
              const isOut = p.total_stock <= 0;
              return (
                <tr
                  key={p.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${highlight === "low" && isLow ? "bg-amber-50/30" : ""} ${canSelect && selected?.has(p.id) ? "bg-emerald-50/40" : ""}`}
                  data-testid={`stock-product-row-${p.id}`}
                >
                  {canSelect && (
                    <td className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => onToggleSelect(p.id)}
                        className="h-4 w-4 accent-emerald-700 cursor-pointer"
                        aria-label={`Select ${p.name} for comparison`}
                        data-testid={`stock-select-${p.id}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-gray-500 font-mono text-xs">{startIndex + i + 1}</td>
                  <td className="px-3 py-2.5"><ThumbCell path={p.image_path} name={p.name} onClick={onImageClick ? () => onImageClick(p.id, p.name) : undefined} /></td>
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-gray-900 leading-tight max-w-[240px] truncate" title={p.name}>
                      <HighlightMatch text={p.name} query={highlightQuery} />
                    </p>
                    <p className="text-[11px] text-gray-500 font-mono">{p.wings_product_id}</p>
                    {p.supplier_aliases && p.supplier_aliases.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5 max-w-[240px]" data-testid={`stock-row-aliases-${p.id}`}>
                        {p.supplier_aliases.slice(0, 2).map((a) => (
                          <span
                            key={a.supplier_id}
                            title={`${a.supplier_name || "Supplier"}: ${a.alias_name}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-medium text-blue-800 truncate max-w-[110px]"
                          >
                            <HighlightMatch text={a.alias_name} query={highlightQuery} />
                          </span>
                        ))}
                        {p.supplier_aliases.length > 2 && (
                          <span className="text-[10px] text-gray-400">+{p.supplier_aliases.length - 2}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-700">{p.unit}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`font-mono font-semibold ${isOut ? "text-red-700" : isLow ? "text-amber-700" : "text-gray-900"}`}>
                      {p.total_stock}
                    </span>
                    {isOut && <StatusBadge variant="error" className="ml-1">Out</StatusBadge>}
                    {!isOut && isLow && <StatusBadge variant="warning" className="ml-1">Low</StatusBadge>}
                  </td>
                  <td className="px-3 py-2.5 bg-amber-50/30">
                    {(p.rg_parents && p.rg_parents.length > 0) ? (
                      <div className="max-w-[200px] space-y-0.5">
                        {p.rg_parents.map((r) => (
                          <div key={r.id} className="flex items-center justify-between gap-2">
                            <p className="text-xs text-gray-900 truncate" title={r.name}>{r.name}</p>
                            <span className={`text-[11px] font-mono font-semibold ${r.stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                              {r.stock ?? 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs font-semibold text-gray-900">{p.code}</td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs text-gray-900">{p.category}</p>
                    <p className="text-[11px] text-gray-500">{p.subcategory}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {p.suppliers.slice(0, 2).map((s) => (
                        <span key={s.id} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 truncate max-w-[140px]" title={s.name}>
                          {s.name}
                        </span>
                      ))}
                      {p.suppliers.length > 2 && (
                        <span className="text-[10px] text-gray-500">+{p.suppliers.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono">{formatINR(p.mrp)}</td>
                  <td className="px-3 py-2.5 text-center bg-amber-50/40">
                    {p.price_code ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-mono text-xs font-semibold tracking-wider">
                        {p.price_code}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{formatDate(p.last_synced_at)}</td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="px-6 py-12 text-center text-sm text-gray-500">No matching products</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={data.total_pages || 1} total={data.total || 0} setPage={setPage} />
    </Surface>
  );
}

function BranchMatrixTable({ matrix, loading, page, setPage, startIndex, onImageClick, highlightQuery, selected, onToggleSelect }) {
  const { branches, rows } = matrix;
  const canSelect = typeof onToggleSelect === "function";
  const scrollRef = useHorizontalScroll();
  return (
    <div className="bg-white border border-gray-200 rounded-md">
      <div
        ref={scrollRef}
        className="relative w-full overflow-auto max-h-[calc(100vh-220px)] outline-none focus:ring-2 focus:ring-[#9B1C1C]/30"
        data-testid="branch-matrix-scroll"
        aria-label="Branch-wise stock matrix — use mouse wheel or Arrow keys to scroll horizontally"
      >
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        )}
        <table className="text-sm border-collapse" style={{ minWidth: "max-content" }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 border-b border-gray-200">
              {canSelect && <th className="px-2 py-3 text-center sticky left-0 bg-gray-50 z-10 w-8"><span className="sr-only">Select</span></th>}
              <th className={`px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider sticky ${canSelect ? "left-8" : "left-0"} bg-gray-50 z-10 w-12`}>#</th>
              <th className={`px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider sticky ${canSelect ? "left-20" : "left-12"} bg-gray-50 z-10`}>Image</th>
              <th className={`px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider sticky ${canSelect ? "left-[124px]" : "left-[92px]"} bg-gray-50 z-10 min-w-[240px]`}>Product</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Unit</th>
              <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/50 min-w-[80px]">Available</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/30 min-w-[160px]">Raw Material</th>
              {branches.map((b) => (
                <th key={b.code} className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[92px]" title={b.name}>
                  {b.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id} className={`border-b border-gray-100 hover:bg-gray-50 ${canSelect && selected?.has(row.id) ? "bg-emerald-50/40" : ""}`} data-testid={`stock-matrix-row-${row.id}`}>
                {canSelect && (
                  <td className={`px-2 py-2.5 text-center sticky left-0 z-10 ${selected?.has(row.id) ? "bg-emerald-50" : "bg-white"}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => onToggleSelect(row.id)}
                      className="h-4 w-4 accent-emerald-700 cursor-pointer"
                      data-testid={`stock-matrix-select-${row.id}`}
                    />
                  </td>
                )}
                <td className={`px-3 py-2.5 text-gray-500 font-mono text-xs sticky ${canSelect ? "left-8" : "left-0"} ${selected?.has(row.id) ? "bg-emerald-50" : "bg-white"}`}>{startIndex + i + 1}</td>
                <td className={`px-3 py-2.5 sticky ${canSelect ? "left-20" : "left-12"} ${selected?.has(row.id) ? "bg-emerald-50" : "bg-white"}`}><ThumbCell path={row.image_path} name={row.name} onClick={onImageClick ? () => onImageClick(row.id, row.name) : undefined} /></td>
                <td className={`px-3 py-2.5 sticky ${canSelect ? "left-[124px]" : "left-[92px]"} ${selected?.has(row.id) ? "bg-emerald-50" : "bg-white"}`}>
                  <p className="font-medium text-gray-900 leading-tight max-w-[240px] truncate" title={row.name}>
                    <HighlightMatch text={row.name} query={highlightQuery} />
                  </p>
                  <p className="text-[11px] text-gray-500 font-mono">{row.code} · {row.category}</p>
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-700">{row.unit}</td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-amber-800 bg-amber-50/30 whitespace-nowrap">{row.total_stock}</td>
                <td className="px-3 py-2.5 bg-amber-50/30">
                  {(row.rg_parents && row.rg_parents.length > 0) ? (
                    <div className="max-w-[200px] space-y-0.5">
                      {row.rg_parents.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2">
                          <p className="text-xs text-gray-900 truncate" title={r.name}>{r.name}</p>
                          <span className={`text-[11px] font-mono font-semibold ${r.stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                            {r.stock ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-300 text-xs">—</span>
                  )}
                </td>
                {branches.map((b) => {
                  const cell = row.branches[b.code] || { qty: 0, damaged: 0 };
                  const isLow = cell.qty > 0 && cell.qty < row.reorder_level;
                  const isOut = cell.qty === 0;
                  const isNegative = cell.qty < 0;
                  return (
                    <td key={b.code} className="px-3 py-2.5 text-right font-mono whitespace-nowrap">
                      <span className={`${isNegative ? "text-red-700" : isOut ? "text-gray-300" : isLow ? "text-amber-700" : "text-gray-900"} font-semibold`}>
                        {cell.qty !== 0 ? cell.qty : "—"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6 + branches.length} className="px-6 py-12 text-center text-sm text-gray-500">No matching products</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} totalPages={matrix.total_pages || 1} total={matrix.total || 0} setPage={setPage} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Group View — side-by-side comparison of hand-picked products.
//
// Fetches the branch-matrix for the exact product_ids (bypasses filters), so
// admins can compare current + per-branch stock across N selected items.
// Prints via window.print scoped to a hidden print area.
// ─────────────────────────────────────────────────────────────────────────────
function GroupViewDialog({ ids, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ branches: [], rows: [] });

  useEffect(() => {
    if (!ids || !ids.length) { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get("/stock/branch-matrix", {
          params: { product_ids: ids.join(","), page: 1, page_size: 500 },
        });
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load comparison");
      } finally {
        setLoading(false);
      }
    })();
  }, [ids]);

  const printCompare = () => {
    const doc = window.open("", "_blank", "noopener,noreferrer,width=1200,height=800");
    if (!doc) { toast.error("Pop-up blocked — enable pop-ups for this site"); return; }
    const branchCols = data.branches.map((b) => `<th>${escapeHtml(b.name)}</th>`).join("");
    const bodyRows = data.rows.map((r) => {
      const cells = data.branches.map((b) => {
        const c = r.branches[b.code] || { qty: 0 };
        return `<td class="num">${c.qty ?? 0}</td>`;
      }).join("");
      return `<tr>
        <td>${escapeHtml(r.name || "")}<br/><span class="mono">${escapeHtml(r.code || "")}</span></td>
        <td>${escapeHtml(r.category || "")}</td>
        <td>${escapeHtml(r.unit || "")}</td>
        <td class="num total">${r.total_stock ?? 0}</td>
        ${cells}
      </tr>`;
    }).join("");
    doc.document.write(`<!doctype html><html><head><title>Stock Comparison — Thara Gold</title>
      <style>
        body { font-family: -apple-system,BlinkMacSystemFont,sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        p.sub { color: #666; font-size: 12px; margin: 0 0 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #f5f5f5; }
        td.num { text-align: right; font-variant-numeric: tabular-nums; }
        td.total { background: #fef7ea; font-weight: 600; }
        .mono { font-family: ui-monospace,SFMono-Regular,monospace; font-size: 10px; color: #666; }
        @media print { body { padding: 12px; } }
      </style></head><body>
      <h1>Stock Comparison — ${data.rows.length} products</h1>
      <p class="sub">Generated ${new Date().toLocaleString()}</p>
      <table><thead><tr>
        <th>Product</th><th>Category</th><th>Unit</th><th class="num">Total</th>${branchCols}
      </tr></thead><tbody>${bodyRows}</tbody></table>
    </body></html>`);
    doc.document.close();
    doc.focus();
    setTimeout(() => doc.print(), 250);
  };

  const exportCsv = () => {
    const header = ["Product", "SKU", "Category", "Sub-Category", "Unit", "Total Stock",
      ...data.branches.map((b) => b.name)];
    const rows = data.rows.map((r) => [
      r.name, r.code || "", r.category || "", r.subcategory || "", r.unit || "", r.total_stock ?? 0,
      ...data.branches.map((b) => (r.branches[b.code]?.qty ?? 0)),
    ]);
    const csv = [header, ...rows]
      .map((cols) => cols.map(csvEscape).join(","))
      .join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-comparison-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="group-view-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Layers2 className="h-5 w-5 text-emerald-700" /> Stock Comparison
              </DialogTitle>
              <DialogDescription>
                Comparing <b>{ids.length}</b> product{ids.length !== 1 ? "s" : ""} across <b>{data.branches.length}</b> active branch{data.branches.length !== 1 ? "es" : ""}.
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || !data.rows.length} className="gap-1.5" data-testid="group-view-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={printCompare} disabled={loading || !data.rows.length} className="gap-1.5" data-testid="group-view-print">
              <Printer className="h-4 w-4" /> Print
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-auto flex-1 px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading comparison…
            </div>
          )}
          {!loading && !data.rows.length && (
            <div className="text-center py-16 text-gray-400 text-sm">No matching products in the comparison set.</div>
          )}
          {!loading && data.rows.length > 0 && (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b-2 border-gray-200 text-left">
                  <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Product</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Category</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Unit</th>
                  <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-amber-800 bg-amber-50/50">Total</th>
                  {data.branches.map((b) => (
                    <th key={b.code} className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-gray-500 whitespace-nowrap" title={b.name}>{b.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50" data-testid={`group-view-row-${r.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-gray-900">{r.name}</div>
                      <div className="text-[11px] text-gray-500 font-mono">{r.code} · {r.subcategory || "—"}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{r.category || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{r.unit || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-amber-800 bg-amber-50/40 tabular-nums">{r.total_stock ?? 0}</td>
                    {data.branches.map((b) => {
                      const c = r.branches[b.code] || { qty: 0 };
                      const q = c.qty ?? 0;
                      const isOut = q === 0;
                      const isNeg = q < 0;
                      return (
                        <td key={b.code} className={`px-3 py-2.5 text-right font-mono tabular-nums ${isNeg ? "text-red-600 font-semibold" : isOut ? "text-gray-300" : "text-gray-800"}`}>
                          {q}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-gray-50">
          <span className="text-xs text-gray-500 mr-auto">Close will clear your current selection.</span>
          <Button variant="outline" onClick={onClose} data-testid="group-view-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

